/**
 * Person Lookup Adapter - Anti-Corruption Layer
 * 
 * This adapter implements the PersonLookupPort for DDD Domain by
 * translating from Party Manager's Published Language to DDD's domain language.
 * 
 * Eric Evans Anti-Corruption Layer pattern:
 * - Protects DDD Domain from Party Manager's model changes
 * - Translates between different ubiquitous languages
 * - Maintains DDD Domain's model integrity
 * 
 * This adapter can be implemented in different ways:
 * - HTTP client calling Party Manager APIs (using Zodios contracts)
 * - Direct database queries (if same database)
 * - Event store projections (if using event sourcing)
 * - Mock implementation for testing
 */

import { injectable } from 'inversify';
import { 
  PersonLookupPort, 
  PersonReference, 
  PersonLookupError 
} from '../../ports/PersonLookupPort';
// Define local types that match Party Manager's Published Language
// This is part of the Anti-Corruption Layer - we define our own types
// instead of importing from Party Manager
interface PersonSummary {
  personId: string;
  displayName: string;
  email: string;
  roleType: 'Human' | 'AiAgent';
  isActive: boolean;
  professionalInfo?: {
    department?: string;
    jobTitle?: string;
  };
}

interface PersonQueryCriteria {
  department?: string;
  roleType?: 'Human' | 'AiAgent';
  searchTerm?: string;
  limit?: number;
}

/**
 * Configuration for the Person Lookup Adapter
 */
export interface PersonLookupAdapterConfig {
  /** Base URL for Party Manager API (if using HTTP) */
  apiBaseUrl?: string;
  /** Connection string for direct DB access (if using database) */
  connectionString?: string;
  /** Enable caching of person lookups */
  enableCaching?: boolean;
  /** Cache TTL in seconds */
  cacheTTL?: number;
}

/**
 * Person Lookup Adapter - Implements PersonLookupPort
 * 
 * This implementation uses Party Manager's Published Language contracts
 * but translates them to DDD Domain's needs through the Anti-Corruption Layer.
 */
@injectable()
export class PersonLookupAdapter implements PersonLookupPort {
  private cache: Map<string, { data: PersonReference; expiry: number }> = new Map();

  constructor(
    private readonly config: PersonLookupAdapterConfig = {}
  ) {}

  /**
   * Find a person by their ID
   * Translates from PersonSummary (Party Manager) to PersonReference (DDD)
   */
  async findPersonById(personId: string): Promise<PersonReference | null> {
    try {
      // Check cache first
      if (this.config.enableCaching) {
        const cached = this.getFromCache(personId);
        if (cached) return cached;
      }

      // TODO: Actual implementation would call Party Manager API or database
      // For now, we'll outline the pattern:
      
      // Option 1: HTTP API call (using Zodios contracts)
      // const client = new Zodios(this.config.apiBaseUrl, partyManagerApi);
      // const response = await client.getPersonById({ params: { id: personId } });
      
      // Option 2: Direct database query
      // const person = await this.queryDatabase(personId);
      
      // Option 3: Event store projection
      // const person = await this.eventStore.getProjection('person', personId);

      // For now, return null to indicate not implemented
      // This follows the "no mocks" rule - real implementation or nothing
      return null;
    } catch (error) {
      throw new PersonLookupError(
        `Failed to find person by ID: ${personId}`,
        error,
        'findPersonById'
      );
    }
  }

  /**
   * Find all persons in a specific department
   */
  async findPersonsByDepartment(department: string): Promise<PersonReference[]> {
    try {
      // TODO: Implement using Party Manager's query API
      // const criteria: PersonQueryCriteria = { department, limit: 100 };
      // const response = await this.queryPersons(criteria);
      // return response.items.map(this.translateToDomainModel);
      
      return [];
    } catch (error) {
      throw new PersonLookupError(
        `Failed to find persons by department: ${department}`,
        error,
        'findPersonsByDepartment'
      );
    }
  }

  /**
   * Find persons by role type (Human vs AiAgent)
   */
  async findPersonsByRoleType(roleType: 'Human' | 'AiAgent'): Promise<PersonReference[]> {
    try {
      // TODO: Implement using Party Manager's query API
      // const criteria: PersonQueryCriteria = { roleType, limit: 100 };
      // const response = await this.queryPersons(criteria);
      // return response.items.map(this.translateToDomainModel);
      
      return [];
    } catch (error) {
      throw new PersonLookupError(
        `Failed to find persons by role type: ${roleType}`,
        error,
        'findPersonsByRoleType'
      );
    }
  }

  /**
   * Search persons by partial name or email
   */
  async searchPersons(searchTerm: string, limit: number = 10): Promise<PersonReference[]> {
    try {
      // TODO: Implement using Party Manager's search API
      // const criteria: PersonQueryCriteria = { searchTerm, limit };
      // const response = await this.queryPersons(criteria);
      // return response.items.map(this.translateToDomainModel);
      
      return [];
    } catch (error) {
      throw new PersonLookupError(
        `Failed to search persons with term: ${searchTerm}`,
        error,
        'searchPersons'
      );
    }
  }

  /**
   * Check if person lookup service is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      // TODO: Implement health check
      // - Check API endpoint health
      // - Check database connection
      // - Check event store connection
      
      return false; // Not implemented yet
    } catch {
      return false;
    }
  }

  /**
   * Anti-Corruption Layer: Translate from Party Manager's model to DDD's model
   * This is where we protect our domain from external changes
   */
  private translateToDomainModel(partyManagerPerson: PersonSummary): PersonReference {
    return {
      personId: partyManagerPerson.personId,
      displayName: partyManagerPerson.displayName,
      email: partyManagerPerson.email,
      roleType: partyManagerPerson.roleType,
      isActive: partyManagerPerson.isActive,
      department: partyManagerPerson.professionalInfo?.department,
      jobTitle: partyManagerPerson.professionalInfo?.jobTitle
    };
  }

  /**
   * Cache management
   */
  private getFromCache(key: string): PersonReference | null {
    const cached = this.cache.get(key);
    if (!cached) return null;
    
    if (Date.now() > cached.expiry) {
      this.cache.delete(key);
      return null;
    }
    
    return cached.data;
  }

  private setCache(key: string, data: PersonReference): void {
    if (!this.config.enableCaching) return;
    
    const expiry = Date.now() + (this.config.cacheTTL || 300) * 1000;
    this.cache.set(key, { data, expiry });
  }
}

/**
 * Factory function for creating PersonLookupAdapter with different implementations
 */
export function createPersonLookupAdapter(
  implementation: 'http' | 'database' | 'mock' | 'eventstore',
  config?: PersonLookupAdapterConfig
): PersonLookupAdapter {
  // This factory allows us to easily switch implementations
  // based on environment or configuration
  
  switch (implementation) {
    case 'http':
      return new PersonLookupAdapter({ 
        ...config, 
        apiBaseUrl: config?.apiBaseUrl || 'http://localhost:3000' 
      });
    
    case 'database':
      return new PersonLookupAdapter({ 
        ...config,
        // Database-specific config
      });
    
    case 'eventstore':
      return new PersonLookupAdapter({ 
        ...config,
        // Event store-specific config
      });
    
    case 'mock':
      // For testing only
      return new PersonLookupAdapter({ 
        ...config,
        enableCaching: false
      });
    
    default:
      return new PersonLookupAdapter(config);
  }
}